import { useEffect, useRef } from 'react'
import { useQueryClient } from '@tanstack/react-query'
import { toast } from 'sonner'
import i18n from '@/i18n'
import type { PowerDialerProspect } from '@/types/powerDialer'

// Horizon max au-delà duquel on ne programme pas de setTimeout. Évite de
// retenir des timers sur 7+ jours qui de toute façon seraient invalidés par
// un reload de l'app.
const HORIZON_MS = 24 * 60 * 60 * 1000 // 24h

/**
 * Programme une browser Notification + toast + refresh de la queue prospects
 * quand l'heure d'un callback est atteinte. Actif uniquement pendant qu'une
 * session Power Dialer est ouverte (l'agent est censé être actif).
 *
 * Si Notification API n'est pas dispo ou permission refusée : on tombe
 * silencieusement sur le toast seul.
 */
export function useCallbackNotifier(
  campaignId: number,
  prospects: PowerDialerProspect[],
  enabled: boolean = true,
) {
  const queryClient = useQueryClient()
  const timeoutsRef = useRef<Map<number, number>>(new Map())

  useEffect(() => {
    if (!enabled) return

    // Demande de permission silencieuse au premier montage. On ne harcèle
    // pas l'utilisateur — s'il refuse, on garde juste le toast.
    if (
      typeof window !== 'undefined' &&
      'Notification' in window &&
      Notification.permission === 'default'
    ) {
      void Notification.requestPermission()
    }

    const now = Date.now()
    const timers = timeoutsRef.current

    // Cleanup des timers obsolètes (prospect plus en callback, ou date changée)
    const validIds = new Set<number>()
    for (const p of prospects) {
      if (p.status !== 'callback' || !p.callback_date) continue
      const ts = new Date(p.callback_date).getTime()
      if (ts <= now) continue // déjà passé → géré par filter ProspectsList
      if (ts - now > HORIZON_MS) continue // trop loin → skip
      validIds.add(p.id)
    }
    for (const [id, handle] of timers.entries()) {
      if (!validIds.has(id)) {
        clearTimeout(handle)
        timers.delete(id)
      }
    }

    // Programme les nouveaux timers (ceux qui ne sont pas déjà programmés)
    for (const p of prospects) {
      if (p.status !== 'callback' || !p.callback_date) continue
      const ts = new Date(p.callback_date).getTime()
      const delay = ts - now
      if (delay <= 0 || delay > HORIZON_MS) continue
      if (timers.has(p.id)) continue

      const handle = window.setTimeout(() => {
        timers.delete(p.id)
        const fullName =
          p.full_name ||
          `${p.first_name || ''} ${p.last_name || ''}`.trim() ||
          p.phone

        // Toast sonner — toujours affiché.
        toast.info(
          i18n.t('powerDialer.callbackNotifier.toastTitle', {
            name: fullName,
            defaultValue: 'Rappel programmé : {{name}}',
          }),
          {
            description: i18n.t(
              'powerDialer.callbackNotifier.toastDescription',
              'Le prospect doit être rappelé maintenant.',
            ),
            duration: 10_000,
          },
        )

        // Browser Notification — best-effort.
        try {
          if (
            'Notification' in window &&
            Notification.permission === 'granted' &&
            document.visibilityState !== 'visible'
          ) {
            new Notification(
              i18n.t(
                'powerDialer.callbackNotifier.notificationTitle',
                'Callizy - Rappel programmé',
              ),
              {
                body: i18n.t('powerDialer.callbackNotifier.notificationBody', {
                  name: fullName,
                  defaultValue: '{{name}} doit être rappelé maintenant.',
                }),
                tag: `pd-callback-${p.id}`,
              },
            )
          }
        } catch {
          // Notification API peut throw sur certains navigateurs/contextes
          // (iframe sans permission, etc.) — on ignore silencieusement.
        }

        // Force le refetch pour que la section "À rappeler maintenant"
        // s'actualise instantanément.
        queryClient.invalidateQueries({
          queryKey: ['power-dialer-prospects', campaignId],
        })
      }, delay)

      timers.set(p.id, handle)
    }
  }, [prospects, enabled, campaignId, queryClient])

  // Cleanup global à l'unmount
  useEffect(() => {
    const timers = timeoutsRef.current
    return () => {
      for (const handle of timers.values()) clearTimeout(handle)
      timers.clear()
    }
  }, [])
}
